import { zodResolver } from '@hookform/resolvers/zod';
import { resetPassword } from 'aws-amplify/auth';
import { useState, type Dispatch, type SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { z } from 'zod';

import { GenericFormScreen } from '@/components/shared/GenericFormScreen/GenericFormScreen';
import { Button } from '@/components/ui/Button/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/Form/Form';
import { Input } from '@/components/ui/Input/Input';
import { toast } from '@/components/ui/Toast/Toast';
import { paths } from '@/config/paths';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';

interface InitializeStepProps {
  goToNextStep: () => void;
  setUsername: Dispatch<SetStateAction<string>>;
}

export const InitializeStep = ({ goToNextStep, setUsername }: InitializeStepProps) => {
  const { t } = useTranslation('auth', { keyPrefix: 'forgotPassword.steps.initialize' });
  const { t: tValidation } = useTranslation('utils', { keyPrefix: 'validation' });
  const { t: tError } = useTranslation('utils', { keyPrefix: 'error' });
  const { translateError } = useErrorTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const formSchema = z.object({
    username: z
      .string()
      .min(1, { message: tValidation('required') })
      .refine((value) => !value.includes(' '), { message: tValidation('noSpace') }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);

    try {
      await resetPassword({ username: values.username.toLowerCase() });
      setUsername(values.username);
      goToNextStep();
    } catch (error) {
      toast({
        variant: 'destructive',
        description: translateError(error, t(`cognitoErrors.${(error as Error)?.name}` as never, tError('GENERAL_ERROR'))),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GenericFormScreen
      title={t('title')}
      description={t('description')}
      leftActionElement={
        <Button type='button' variant='link' asChild>
          <Link to={paths.auth.login.path}>{t('back.label')}</Link>
        </Button>
      }
      rightActionElement={
        <Button type='submit' form='forgot-password-initialize-form' isLoading={isLoading} disabled={isLoading} aria-busy={isLoading}>
          {t('submit.label')}
        </Button>
      }
    >
      <Form {...form}>
        <form
          id='forgot-password-initialize-form'
          onSubmit={(e) => {
            void form.handleSubmit(onSubmit)(e);
          }}
          className='mb-10 flex flex-col gap-4'
        >
          <FormField
            control={form.control}
            name='username'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('username.label')}</FormLabel>
                <FormControl>
                  <Input {...field} autoComplete='username' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
      <Button variant='link' asChild className='text-text-brand-primary px-0'>
        <Link to={paths.auth.forgotUsername.path}>{t('forgotUsername.label')}</Link>
      </Button>
    </GenericFormScreen>
  );
};
